import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { TokenService } from '../services/token.service';

function loginTree(router: Router, state: RouterStateSnapshot) {
  const returnUrl = state.url.startsWith('/auth/login')
    ? '/'
    : state.url.split('?')[0];
  return router.createUrlTree(['/auth/login'], {
    queryParams: { returnUrl },
  });
}

/** Blocks app routes when no tenant subdomain is resolved. */
function ensureTenant(router: Router): ReturnType<CanActivateFn> | null {
  const tenantService = inject(TenantService);

  if (tenantService.hasTenant()) {
    return null;
  }

  return router.createUrlTree(['/auth/login'], {
    queryParams: { error: 'tenant_required' },
  });
}

/** Validates tenant + JWT session for protected routes. */
export const authGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const tenantRedirect = ensureTenant(router);

  if (tenantRedirect) {
    return tenantRedirect;
  }

  const authService = inject(AuthService);
  const tokenService = inject(TokenService);

  if (!tokenService.hasToken()) {
    return loginTree(router, state);
  }

  if (tokenService.isTokenExpired()) {
    authService.discardLocalSession();
    return router.createUrlTree(['/auth/login']);
  }

  if (authService.isAuthenticated()) {
    return true;
  }

  return authService.getCurrentUser().pipe(
    map(() => true),
    catchError(() => {
      authService.discardLocalSession();
      return of(router.createUrlTree(['/auth/login']));
    }),
  );
};

/** Keeps login/signup pages guest-only; sends authenticated users to role home. */
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);

  if (!tokenService.hasToken() || tokenService.isTokenExpired()) {
    return true;
  }

  // Token exists but may belong to a different portal (e.g. super-admin token in tenant portal).
  // Clear it immediately to avoid an infinite redirect loop.
  if (!authService.resolveAccountType()) {
    authService.discardLocalSession();
    return true;
  }

  if (authService.isAuthenticated()) {
    authService.navigateToRoleHome();
    return false;
  }

  return authService.getCurrentUser().pipe(
    map(() => {
      if (!authService.resolveAccountType()) {
        authService.discardLocalSession();
        return true;
      }
      authService.navigateToRoleHome();
      return false;
    }),
    catchError(() => {
      authService.discardLocalSession();
      return of(true);
    }),
  );
};
